import * as React from "react";
import { Box, Grid, Card, Typography, ToggleButton, ToggleButtonGroup, Tooltip, IconButton } from "@mui/material";
import CardContent from '@mui/material/CardContent';
import Plot from "react-plotly.js";
import useWindowDimensions from "../../util/useWindowDimensions";
import { groupBy, meanBy, minBy, maxBy, sumBy, first, mean } from 'lodash';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import EditIcon from '@mui/icons-material/Edit';
import AddCommentIcon from '@mui/icons-material/AddComment';
import { changeTimezone, monthAndYearToShortString } from "../../util/dateFunctions";
import { linearRegression } from "../../util/calcs";
import AnnotationDialog from "../../common/AnnotationDialog";

const GambitRte = ({ accessToken, kpiMetricsFullYear, plant, plantId, month, year, plantAnnotations, appConfig, refreshPlantAnnotations }) => {
    const { windowHeight, windowWidth } = useWindowDimensions();
    const [annotationDialogOpen, setAnnotationDialogOpen] = React.useState(false);
    const [annotationToEdit, setAnnotationToEdit] = React.useState(null);
    const [monthYearValues, setMonthYearValues] = React.useState(null);
    const [rtePct, setRtePct] = React.useState(null);
    const [throughputMwhs, setThroughputMwhs] = React.useState(null);
    
    React.useEffect(() => {
        setMonthYearValues(
            kpiMetricsFullYear.map(entry => {
            return monthAndYearToShortString(entry.month, entry.year);
        })
        );
        setRtePct(
            kpiMetricsFullYear.map((entry) => entry.RTE)
        );
        setThroughputMwhs(
            kpiMetricsFullYear.map((entry) => entry.Throughput)
        );
    }, [kpiMetricsFullYear]);

    const getObservationsAndRecommendations = () => {
        if (plantAnnotations == null) {
            return [];
        } else {
            let ret = plantAnnotations.filter(function (annotation) {
                return annotation.AnnotationLocation == "RTEOBS" && (annotation.AnnotationType == "OBS" || annotation.AnnotationType == "REC");
            });

            if (appConfig.canAccessInternalAnnotations == null || appConfig.canAccessInternalAnnotations == false) {
                ret = ret.filter(function (annotation) {
                    return annotation.InternalOnly == false;
                });
            }
            ret = ret.sort(function (a, b) {
                return a.SortOrder < b.SortOrder ? -1 : 1;
            })
            return ret;
        }
    };

    const handleNewComment = () => {
        setAnnotationToEdit(null);
        setAnnotationDialogOpen(true);
    };

    const handleEditComment = (annotation) => {
        setAnnotationToEdit(annotation);
        setAnnotationDialogOpen(true);
    };

    const handleCloseAnnotationDialog = () => {
        setAnnotationDialogOpen(false);
    }

    return (
        <Grid container spacing={0}>
        <Box>
        <Plot
            data={[
                {
                x: monthYearValues,
                y: rtePct,
                name: "Operational RTE",
                type: "bar",
                marker: {
                    color: "rgba(45,156,138,1)"
                },
                yaxis: "y1",
                },
                {
                x: monthYearValues,
                y: throughputMwhs,
                name: "Throughput",
                type: "scatter",
                mode: "lines",
                line: {
                    color: "rgba(245,81,81,1)",
                    width: 6
                },
                yaxis: "y2",
                },
            ]}
            layout={{
                xaxis: {

                },
                yaxis: {
                    title: {
                        text: 'Operational RTE %'
                    }
                },
                yaxis2: {
                    title: {
                        text: 'Throughput MWh'
                    },
                    anchor: "x",
                    overlaying: "y",
                    side: "right",
                    autorange: true 
                },
                annotations: [
                    {
                        x: 1.10,
                        xanchor: 'right',
                        y: 1.0,
                        yanchor: 'bottom',
                        xref: 'paper',
                        yref: 'paper',
                        text: 'Average ORTE (Year): <b>'+Math.round(mean(rtePct)*10)/10+'%</b><br>Average ORTE (Life): <b>76.8%</b> <br>Guaranteed RTE: <b>82.9%</b>',
                        showarrow: false,
                        font: {
                            size: 20,
                            color: 'black'
                          },
                        align: 'left',
                        bordercolor: 'black',
                        borderwidth: 3,
                        borderpad: 4, 
                        bgcolor: 'white',
                        opacity: 0.9
                    }
                ],
                template: "simple_white",
                uirevision: 'true',
                title: {
                    text: 'Operational RTE',
                    font: {
                        size: 20
                    }
                },
                width: windowWidth - 165,
                height: windowHeight - 350,
            }}
        />
        </Box>
        <Grid>
        <Card elevation={0}>
                <CardContent>
                    <Typography sx={{ fontSize: 28, fontWeight: 'bold', textDecoration: 'underline' }}>
                        Observations & Recommendations {appConfig.canAccessInternalAnnotations ? (<IconButton onClick={handleNewComment}><AddCommentIcon/></IconButton>) : null}
                    </Typography>
                            {
                                getObservationsAndRecommendations().map((annotation) => {
                                    return (
                                        <React.Fragment>
                                            <Typography sx={{ fontSize: 16 }}>&nbsp;</Typography>
                                            <Typography sx={{ fontSize: 22 }}>
                                                {annotation.InternalOnly ? <Tooltip title='Only visible to internal personnel'><AdminPanelSettingsIcon /></Tooltip> : null}
                                                {annotation.AnnotationType == "OBS" ? "Observation:" : "Recommendation:"}
                                                <Typography sx={{ fontSize: 22 }} display="inline"> {annotation.AnnotationText} </Typography>
                                                {appConfig.canAccessInternalAnnotations ? (<IconButton onClick={() => handleEditComment(annotation)}><EditIcon /></IconButton>) : null}
                                            </Typography>
                                        </React.Fragment>
                                    )
                                }
                                )
                            }
                        </CardContent>
                    </Card>
                <AnnotationDialog accessToken={accessToken} open={annotationDialogOpen} handleClose={handleCloseAnnotationDialog} annotationLocation={'RTEOBS'} selectableAnnotationTypes={['OBS', 'REC']} enableSortOrder={true} assetType={'plant'} assetId={plantId} month={month} year={year} appConfig={appConfig} toEdit={annotationToEdit} refreshPlantAnnotations={refreshPlantAnnotations} />
        </Grid>
    </Grid>
    );
};

export default GambitRte;
